// Devise 認証画面用 Vite エントリポイント。
// 保護者登録フォームの「選手検索」UI と、利用規約の公開ページ（/terms）の本文描画を扱う。

import { LEGAL_TABS } from "../../javascript/features/legal/legalContents";

// grade_label / months / amount は保護者登録（利用料の見積もり）のときだけ入る
type Player = {
  id: number;
  name: string;
  furigana: string;
  grade_label?: string;
  months?: number;
  amount?: number;
};

const yen = (value: number): string => `${value.toLocaleString("ja-JP")}円`;

function initPlayerSearch(root: HTMLElement): void {
  const players: Player[] = JSON.parse(root.dataset.players || "[]");
  const initialSelected: number[] = JSON.parse(root.dataset.selectedIds || "[]");

  const chipsEl = root.querySelector<HTMLElement>("[data-player-search-chips]");
  const inputEl = root.querySelector<HTMLInputElement>("[data-player-search-input]");
  const candidatesEl = root.querySelector<HTMLElement>("[data-player-search-candidates]");
  const summaryEl = root.querySelector<HTMLElement>("[data-player-search-summary]");
  if (!chipsEl || !inputEl || !candidatesEl) return;

  const selectedIds = new Set<number>(initialSelected.map(Number));

  const displayName = (p: Player): string =>
    (p.name && p.name.trim()) || (p.furigana && p.furigana.trim()) || "";

  const findPlayer = (id: number): Player | undefined =>
    players.find((p) => Number(p.id) === id);

  // 例: "高2・18ヶ月・9,000円"（見積もりが無い画面では空）
  const quoteLabel = (p: Player): string =>
    p.months != null && p.amount != null
      ? [p.grade_label, `${p.months}ヶ月`, yen(p.amount)].filter(Boolean).join("・")
      : "";

  const renderSummary = (): void => {
    if (!summaryEl) return;

    const selected = Array.from(selectedIds)
      .map(findPlayer)
      .filter((p): p is Player => p != null && p.months != null && p.amount != null);
    summaryEl.hidden = selected.length === 0;
    if (selected.length === 0) return;

    const months = selected.reduce((sum, p) => sum + (p.months ?? 0), 0);
    const amount = selected.reduce((sum, p) => sum + (p.amount ?? 0), 0);
    summaryEl.textContent = `お支払い合計：${yen(amount)}（${months}ヶ月分）`;
  };

  const renderChips = (): void => {
    chipsEl.innerHTML = "";
    selectedIds.forEach((id) => {
      const player = findPlayer(id);
      if (!player) return;

      const chip = document.createElement("span");
      chip.className = "player-search__chip";

      const label = document.createElement("span");
      label.className = "player-search__chip-label";
      const quote = quoteLabel(player);
      label.textContent = quote ? `${displayName(player)}（${quote}）` : displayName(player);
      chip.appendChild(label);

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "player-search__chip-remove";
      removeBtn.setAttribute("aria-label", `${displayName(player)}を削除`);
      removeBtn.textContent = "×";
      removeBtn.addEventListener("click", () => {
        selectedIds.delete(id);
        renderChips();
        renderCandidates(inputEl.value);
      });
      chip.appendChild(removeBtn);

      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "child_user_ids[]";
      hidden.value = String(id);
      chip.appendChild(hidden);

      chipsEl.appendChild(chip);
    });
    renderSummary();
  };

  const renderCandidates = (query: string): void => {
    const trimmed = query.trim().toLowerCase();
    candidatesEl.innerHTML = "";

    if (trimmed === "") {
      candidatesEl.hidden = true;
      return;
    }

    const matches = players.filter((p) => {
      if (selectedIds.has(Number(p.id))) return false;
      const name = (p.name || "").toLowerCase();
      const furigana = (p.furigana || "").toLowerCase();
      return name.includes(trimmed) || furigana.includes(trimmed);
    });

    candidatesEl.hidden = false;

    if (matches.length === 0) {
      const empty = document.createElement("li");
      empty.className = "player-search__empty";
      empty.textContent = "該当する選手がいません";
      candidatesEl.appendChild(empty);
      return;
    }

    matches.forEach((player) => {
      const li = document.createElement("li");
      li.className = "player-search__candidate";
      li.textContent = displayName(player);
      const quote = quoteLabel(player);
      if (quote) {
        const meta = document.createElement("span");
        meta.className = "player-search__candidate-meta";
        meta.textContent = quote;
        li.appendChild(meta);
      }
      li.addEventListener("click", () => {
        const pid = Number(player.id);
        if (selectedIds.has(pid)) return;
        selectedIds.add(pid);
        renderChips();
        inputEl.value = "";
        renderCandidates("");
        inputEl.focus();
      });
      candidatesEl.appendChild(li);
    });
  };

  inputEl.addEventListener("input", (e) => {
    renderCandidates((e.target as HTMLInputElement).value);
  });

  renderChips();
  renderCandidates("");
}

// 利用規約の本文は SPA の規約等ページと同じ legalContents.ts を唯一の情報源にする
function renderLegalTerms(root: HTMLElement): void {
  const terms = LEGAL_TABS.find((tab) => tab.key === "terms");
  if (!terms) return;

  const updatedEl = root.querySelector<HTMLElement>("[data-legal-terms-updated]");
  const bodyEl = root.querySelector<HTMLElement>("[data-legal-terms-body]");
  if (updatedEl) updatedEl.textContent = `最終更新日：${terms.updatedOn}`;
  if (bodyEl) bodyEl.textContent = terms.body;
}

function init(): void {
  document
    .querySelectorAll<HTMLElement>("[data-player-search-root]")
    .forEach(initPlayerSearch);
  document.querySelectorAll<HTMLElement>("[data-legal-terms]").forEach(renderLegalTerms);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
